import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_HOST } from "../../constants";
import { useSession } from "../Login/SessionContext";
import LoadingAnime from "../Common/LoadingAnime";
import Button from "../ui/Button";
import Select from "../ui/Select";
import { isAdminUser } from "../../utils/userUtils";
import { getCookie } from "../../utils/cookieUtil";
import Pagination from "../ui/Pagination";

function AdminDashboard() {
  const { user, isLoading } = useSession();
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [hiddenPosts, setHiddenPosts] = useState([]);
  const [hiddenPage, setHiddenPage] = useState(1);
  const [hiddenTotalPages, setHiddenTotalPages] = useState(1);
  const [tagSettings, setTagSettings] = useState({ period_days: 30, limit: 50 });
  const [moduleUpdates, setModuleUpdates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const requests = [
          axios.get(`${API_HOST}/api/modules/`, { withCredentials: true }),
          axios.get(`${API_HOST}/api/admin/users/`, { withCredentials: true }),
          axios.get(`${API_HOST}/api/bbs/`, { withCredentials: true }),
          axios.get(`${API_HOST}/api/comments/`, { withCredentials: true }),
          axios.get(`${API_HOST}/api/admin/bbs/hidden/`, { withCredentials: true }),
        ];

        const [modulesRes, usersRes, postsRes, commentsRes, hiddenRes] = await Promise.allSettled(requests);

        if (modulesRes.status === "fulfilled") setModules(modulesRes.value.data);
        if (usersRes.status === "fulfilled") setUsers(usersRes.value.data);
        if (postsRes.status === "fulfilled") setPosts(postsRes.value.data);
        if (commentsRes.status === "fulfilled") setComments(commentsRes.value.data);
        if (hiddenRes.status === "fulfilled") {
          setHiddenPosts(hiddenRes.value.data.results || []);
          setHiddenTotalPages(hiddenRes.value.data.total_pages || 1);
          setHiddenPage(hiddenRes.value.data.current_page || 1);
        }

        if ([modulesRes, usersRes, postsRes, commentsRes, hiddenRes].every((res) => res.status === "rejected")) {
          setError("관리자 데이터를 불러오지 못했습니다.");
        }
      } catch (err) {
        setError("관리자 데이터를 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    const fetchTagSettings = async () => {
      try {
        const response = await axios.get(`${API_HOST}/api/admin/tags/settings/`, { withCredentials: true });
        setTagSettings(response.data);
      } catch (err) {
        // ignore if not available
      }
    };
    fetchTagSettings();
  }, []);

  const handleModuleChange = (moduleId, value) => {
    setModuleUpdates((prev) => ({ ...prev, [moduleId]: value }));
  };

  const fetchHiddenPosts = async (page = 1) => {
    const response = await axios.get(`${API_HOST}/api/admin/bbs/hidden/`, {
      withCredentials: true,
      params: { page },
    });
    setHiddenPosts(response.data.results || []);
    setHiddenTotalPages(response.data.total_pages || 1);
    setHiddenPage(page);
  };

  const handleRestoreHidden = async (postId) => {
    try {
      const csrfToken = getCookie("csrftoken");
      await axios.patch(
        `${API_HOST}/api/admin/bbs/hidden/${postId}/restore/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      await fetchHiddenPosts(hiddenPage);
    } catch (err) {
      alert("숨김 글 복구에 실패했습니다.");
    }
  };

  const handlePurgeHidden = async (postId) => {
    if (!window.confirm("이 게시글을 영구 삭제하시겠습니까?")) return;
    try {
      const csrfToken = getCookie("csrftoken");
      await axios.delete(`${API_HOST}/api/admin/bbs/hidden/${postId}/purge/`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        withCredentials: true,
      });
      await fetchHiddenPosts(hiddenPage);
    } catch (err) {
      alert("숨김 글 삭제에 실패했습니다.");
    }
  };

  const handleSaveModules = async () => {
    const updates = Object.entries(moduleUpdates);
    if (!updates.length) return;

    try {
      const csrfToken = getCookie("csrftoken");
      await Promise.all(
        updates.map(([moduleId, noticeLimit]) =>
          axios.patch(
            `${API_HOST}/api/modules/${moduleId}/`,
            { notice_limit: noticeLimit },
            {
              headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": csrfToken,
              },
              withCredentials: true,
            }
          )
        )
      );
      setModuleUpdates({});
      const modulesRes = await axios.get(`${API_HOST}/api/modules/`);
      setModules(modulesRes.data);
      alert("공지 노출 설정이 저장되었습니다.");
    } catch (err) {
      alert("공지 설정 저장에 실패했습니다.");
    }
  };

  const handleSaveTagSettings = async () => {
    try {
      const csrfToken = getCookie("csrftoken");
      const response = await axios.patch(
        `${API_HOST}/api/admin/tags/settings/`,
        tagSettings,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      setTagSettings(response.data);
      alert("태그 집계 설정이 저장되었습니다.");
    } catch (err) {
      alert("태그 집계 설정 저장에 실패했습니다.");
    }
  };

  if (isLoading || loading) return <LoadingAnime label="관리자 데이터를 불러오는 중..." />;

  if (!isAdminUser(user)) {
    return (
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">관리자 전용</h2>
        <p className="mt-2 text-sm text-ink-200">이 페이지는 관리자만 접근할 수 있습니다.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">오류</h2>
        <p className="mt-2 text-sm text-ink-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="glass-card p-6">
        <h1 className="font-display text-3xl text-white">관리자 대시보드</h1>
        <p className="mt-2 text-sm text-ink-200">게시글, 댓글, 사용자 활동을 한 곳에서 관리합니다.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" disabled>
            일괄 게시글 숨김 (준비 중)
          </Button>
          <Button variant="secondary" disabled>
            스팸 댓글 정리 (준비 중)
          </Button>
          <Button variant="secondary" disabled>
            사용자 일괄 점검 (준비 중)
          </Button>
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">게시판 모듈 관리</h2>
            <p className="text-xs text-ink-200">공지 노출 개수와 활성 상태를 점검하세요.</p>
          </div>
          <Button variant="secondary" onClick={handleSaveModules}>
            공지 설정 저장
          </Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {modules.map((module) => (
            <div
              key={module.id}
              className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-ink-200"
            >
              <h3 className="text-base font-semibold text-white">{module.name || module.module_name}</h3>
              <div className="mt-3">
                <p className="text-xs text-ink-200">공지 노출 개수</p>
                <Select
                  value={moduleUpdates[module.id] ?? module.notice_limit ?? 5}
                  onChange={(event) => handleModuleChange(module.id, Number(event.target.value))}
                  options={[5, 10, 15, 20].map((value) => ({ value, label: `${value}개` }))}
                />
              </div>
              <p className="mt-3 text-xs text-ink-200">상태: {module.is_active ? "활성" : "비활성"}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">태그 집계 설정</h2>
            <p className="text-xs text-ink-200">트렌드 태그 집계 기간과 노출 개수를 설정합니다.</p>
          </div>
          <Button variant="secondary" onClick={handleSaveTagSettings}>
            태그 설정 저장
          </Button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-ink-200">
            <p className="text-xs text-ink-200">집계 기간</p>
            <Select
              value={tagSettings.period_days}
              onChange={(event) =>
                setTagSettings((prev) => ({ ...prev, period_days: Number(event.target.value) }))
              }
              options={[
                { value: 7, label: "최근 7일" },
                { value: 30, label: "최근 30일" },
                { value: 90, label: "최근 90일" },
                { value: 0, label: "전체 기간" },
              ]}
            />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-ink-200">
            <p className="text-xs text-ink-200">노출 개수</p>
            <Select
              value={tagSettings.limit}
              onChange={(event) =>
                setTagSettings((prev) => ({ ...prev, limit: Number(event.target.value) }))
              }
              options={[20, 30, 50, 100].map((value) => ({ value, label: `${value}개` }))}
            />
          </div>
        </div>
      </section>

      <section className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">최근 게시글</h2>
        <p className="mt-2 text-sm text-ink-200">최근 작성된 게시글을 빠르게 확인합니다.</p>
        <div className="mt-4 space-y-2 text-sm text-ink-200">
          {posts.slice(0, 6).map((post) => (
            <div key={post.document_id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div>
                <p className="text-white">{post.title}</p>
                <p className="text-xs text-ink-200">작성자 {post.author_nickname}</p>
              </div>
              <span className="text-xs text-ink-200">조회 {post.view_count}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">최근 댓글</h2>
        <p className="mt-2 text-sm text-ink-200">빠른 대응이 필요한 댓글을 확인하세요.</p>
        <div className="mt-4 space-y-2 text-sm text-ink-200">
          {comments.slice(0, 6).map((comment) => (
            <div key={comment.id} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <p className="text-white">{comment.content}</p>
              <p className="text-xs text-ink-200">작성자 {comment.author_nickname || "익명"}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">숨김 게시글</h2>
            <p className="mt-2 text-sm text-ink-200">복구 또는 영구 삭제가 가능합니다.</p>
          </div>
          <Button variant="secondary" onClick={() => fetchHiddenPosts(hiddenPage)}>
            새로고침
          </Button>
        </div>
        <div className="mt-4 space-y-2 text-sm text-ink-200">
          {hiddenPosts.length === 0 ? (
            <p className="text-sm text-ink-200">숨김 처리된 게시글이 없습니다.</p>
          ) : (
            hiddenPosts.map((post) => (
              <div
                key={post.document_id}
                className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-white">{post.title}</p>
                  <p className="text-xs text-ink-200">
                    모듈 {post.board_module_name || "-"} · 작성자 {post.author_nickname || "-"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => handleRestoreHidden(post.document_id)}>
                    복구
                  </Button>
                  <Button variant="danger" onClick={() => handlePurgeHidden(post.document_id)}>
                    삭제
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="mt-4">
          <Pagination totalPages={hiddenTotalPages} currentPage={hiddenPage} onPageChange={fetchHiddenPosts} />
        </div>
      </section>

      <section className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white">사용자 관리</h2>
        <p className="mt-2 text-sm text-ink-200">최근 가입한 사용자를 확인하고 권한을 점검하세요.</p>
        <div className="mt-4 space-y-2 text-sm text-ink-200">
          {users.slice(0, 8).map((member) => (
            <div key={member.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div>
                <p className="text-white">{member.nickname || member.username}</p>
                <p className="text-xs text-ink-200">{member.email || "-"}</p>
              </div>
              <span className="text-xs text-ink-200">{member.is_superuser || member.is_staff ? "관리자" : "일반"}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AdminDashboard;
